import {
  Box,
  Typography,
  useInterstateToken
} from "@dealer-center/core-components";
import type { NrtStatus } from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";

export interface StatusChipProps {
  status: NrtStatus;
}

// TESTING doesn't have a confirmed Interstate token yet, so it falls back to
// a hand-picked neutral tone pending design system guidance. #946200 was
// checked against its own ~15% tint background (the chip's actual rendered
// background, per the color-mix/hex-alpha fallback below) and only clears
// ~4.27:1 — short of the 5:1 text-contrast bar in .claude/rules/
// accessibility.md. #6b4500 (same amber family, darker) clears ~6.64:1
// against that same tint.
const FALLBACK_COLOR: Record<NrtStatus, string> = {
  ACTIVE: "#0f7a4d",
  INACTIVE: "#b3261e",
  TESTING: "#6b4500"
};

// ACTIVE/INACTIVE read off Interstate's own success/error semantic tokens so
// the chip stays in sync with theme changes and meets the design system's
// contrast guarantees, instead of hardcoded hex values.
const getStatusColor = (
  status: NrtStatus,
  getToken: ReturnType<typeof useInterstateToken>
): string => {
  if (status === "ACTIVE") {
    return (
      getToken<string>("sem.color.on-surface.success.default") ??
      FALLBACK_COLOR.ACTIVE
    );
  }
  if (status === "INACTIVE") {
    return (
      getToken<string>("sem.color.on-surface.error.default") ??
      FALLBACK_COLOR.INACTIVE
    );
  }
  return FALLBACK_COLOR[status];
};

export const StatusChip = ({ status }: StatusChipProps) => {
  const getToken = useInterstateToken();
  const color = getStatusColor(status, getToken);

  return (
    <Box
      sx={{
        // Plain hex-alpha (~15% opacity) fallback first: color-mix() isn't
        // supported in Chrome < 111, Safari < 16.2, or Firefox < 113, and an
        // unsupported value makes the whole backgroundColor declaration a
        // no-op — losing the pill shape entirely rather than just degrading.
        // Declaration order matters here: this fallback must come BEFORE the
        // "@supports" override below so browsers that do understand
        // color-mix() apply that later, more-specific-by-source-order rule
        // instead of this one.
        backgroundColor: `${color}26`,
        "@supports (background-color: color-mix(in srgb, red 15%, white))": {
          backgroundColor: `color-mix(in srgb, ${color} 15%, white)`
        },
        borderRadius: "999px",
        display: "inline-block",
        padding: "0.15rem 0.75rem"
      }}
    >
      <Typography sx={{ color, fontWeight: 600 }} variant="body-sm">
        {status}
      </Typography>
    </Box>
  );
};
